/**
 * useWorkspaceEvents — Singleton SSE hook for unified workspace events.
 *
 * Opens a single persistent connection to GET /api/v1/workspace/events.
 * Parses SSE events and dispatches them via a callback.
 * Auto-reconnects with exponential backoff on disconnect.
 */

import { useEffect, useRef, useCallback } from 'react';
import { getApiUrl, STORAGE_KEYS } from '../api/config';
import { handleUnauthorizedResponse } from '../api/client';
import { readSseStream } from '../api/sse';

export interface WorkspaceEvent {
  /** Redis stream entry ID */
  id: string;
  /** Event type (job.started, job.progress, job.completed, job.failed, job.cancelled) */
  type: string;
  /** Category (status, stream, result, error, system) */
  category: string;
  /** Job ID */
  jobId: string;
  /** Correlation ID */
  correlationId: string;
  /** Sequence number */
  sequence: number;
  /** Event-specific data payload */
  data: Record<string, unknown>;
  /** ISO timestamp */
  timestamp: string;
}

export type WorkspaceEventHandler = (event: WorkspaceEvent) => void;

interface UseWorkspaceEventsOptions {
  /** Whether the connection should be active */
  enabled?: boolean;
  /** Callback for each event */
  onEvent: WorkspaceEventHandler;
  /** Called when connection opens */
  onOpen?: () => void;
  /** Called when connection drops (before reconnect) */
  onDisconnect?: () => void;
}

const MAX_RECONNECT_DELAY_MS = 30_000;
const INITIAL_RECONNECT_DELAY_MS = 1_000;

/**
 * Hook that maintains a persistent SSE connection to the workspace events endpoint.
 * Should be used once at the app level (inside WorkspaceEventsProvider).
 */
export function useWorkspaceEvents(options: UseWorkspaceEventsOptions): void {
  const { enabled = true, onEvent, onOpen, onDisconnect } = options;

  // Use refs for callbacks to avoid effect re-runs on callback identity changes
  const onEventRef = useRef(onEvent);
  const onOpenRef = useRef(onOpen);
  const onDisconnectRef = useRef(onDisconnect);
  onEventRef.current = onEvent;
  onOpenRef.current = onOpen;
  onDisconnectRef.current = onDisconnect;

  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEventIdRef = useRef<string | undefined>(undefined);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);
  const mountedRef = useRef(true);

  const connect = useCallback(async () => {
    if (!mountedRef.current) return;

    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) return;

    // Abort any existing connection
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const url = getApiUrl('/workspace/events');
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
      'Accept': 'text/event-stream',
      'X-Correlation-ID': crypto.randomUUID(),
    };
    if (lastEventIdRef.current) {
      headers['Last-Event-ID'] = lastEventIdRef.current;
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
        signal: abortController.signal,
      });

      if (!response.ok) {
        if (handleUnauthorizedResponse(response.status)) return;
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('SSE response has no body');
      }

      // Connection established — reset backoff
      reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
      onOpenRef.current?.();

      await readSseStream(response.body, ({ id, event, data }) => {
        if (abortController.signal.aborted) return;
        if (id) lastEventIdRef.current = id;
        if (event) {
          try {
            onEventRef.current(JSON.parse(data) as WorkspaceEvent);
          } catch {
            // Skip malformed events
          }
        }
      });
    } catch (err) {
      if (abortController.signal.aborted) return;

      // Connection lost — schedule reconnect
      onDisconnectRef.current?.();
    }

    // Reconnect with exponential backoff
    if (mountedRef.current && !abortController.signal.aborted) {
      const delay = reconnectDelayRef.current;
      reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY_MS);

      reconnectTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current) connect();
      }, delay);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      connect();
    }

    return () => {
      mountedRef.current = false;
      abortControllerRef.current?.abort();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [enabled, connect]);
}